import React, { useState, useEffect, useCallback } from 'react';
import { SearchOutlined, EnvironmentOutlined, CheckOutlined } from '@ant-design/icons';
import { DownOutlined } from '@ant-design/icons';
import { useAuth } from '../../../../../contexts/AuthContext';
import axios from '../../../../../utils/axiosConfig';

const SelectLocations = ({ selectedLocations, onUpdate, onNext, clientId, entityType, isAdminCreating }) => {
  const { currentUser, token } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false); // eslint-disable-line no-unused-vars
  
  // Determine if user is enterprise (multi-location) or small business (single location)
  // When admin is creating, use entityType prop
  // Otherwise use currentUser role
  const isEnterprise = isAdminCreating ? entityType === 'enterprise' : currentUser?.role === 'agency';
  const isSingleLocation = isAdminCreating ? entityType === 'client' : currentUser?.role === 'client';

  const authHeader = useCallback(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  // Fetch locations from API
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/vehicle-tracking/workspaces', { headers: authHeader() });
      const workspaces = Array.isArray(data) ? data : (data.items || []);
      // Transform to location format
      const transformedLocations = workspaces.map(ws => ({
        id: ws.id,
        name: ws.name,
        address: ws.address || 'Location',
        state: ws.state || '',
        city: ws.city || ''
      }));
      setLocations(transformedLocations);
      
      // Auto-select for single location users or if only one location exists
      if ((isSingleLocation || transformedLocations.length === 1) && transformedLocations.length > 0) {
        onUpdate([transformedLocations[0].id]);
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeader, isSingleLocation, onUpdate]);

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const states = ['all', ...new Set(locations.map(l => l.state))];
  const cities = ['all', ...new Set(locations.filter(l => stateFilter === 'all' || l.state === stateFilter).map(l => l.city))];

  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          location.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesState = stateFilter === 'all' || location.state === stateFilter;
    const matchesCity = cityFilter === 'all' || location.city === cityFilter;
    return matchesSearch && matchesState && matchesCity;
  });

  const toggleLocation = (locationId) => {
    const isSelected = selectedLocations.includes(locationId);
    if (isSelected) {
      onUpdate(selectedLocations.filter(id => id !== locationId));
    } else {
      onUpdate([...selectedLocations, locationId]);
    }
  };

  const selectAll = () => {
    const allIds = filteredLocations.map(l => l.id);
    onUpdate(allIds);
  };

  const handleContinue = () => {
    if (selectedLocations.length > 0) {
      onNext();
    }
  };

  return (
    <div className="step-container">
      <h1 className="step-title">Select Locations</h1>
      <p className="step-subtitle">
        {isSingleLocation 
          ? 'Your business location for this campaign' 
          : 'Choose one or more business locations for this campaign'}
      </p>

      <div className="locations-container">
        {/* Info message for single location users */}
        {isSingleLocation && (
          <div style={{ 
            background: '#e6f4ff', 
            border: '1px solid #91d5ff', 
            borderRadius: '8px', 
            padding: '12px 16px', 
            marginBottom: '24px',
            fontSize: '14px',
            color: 'var(--ink)'
          }}>
            ℹ️ Your business location has been automatically selected for this campaign.
          </div>
        )}
        
        {/* Filters - Only show for enterprise users */}
        {isEnterprise && (
        <div className="locations-filters">
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <SearchOutlined style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search locations..."
              className="search-input"
              style={{ paddingLeft: 36 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select 
            className="filter-select"
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setCityFilter('all');
            }}
          >
            <option value="all">All States</option>
            {states.filter(s => s !== 'all').map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>

          <select 
            className="filter-select"
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
          >
            <option value="all">All Cities</option>
            {cities.filter(c => c !== 'all').map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>

          <button className="select-all-btn" onClick={selectAll}>
            Select All ({filteredLocations.length})
          </button>
        </div>
        )}

        {/* Location Cards */}
        <div className="locations-grid">
          {filteredLocations.map(location => {
            const isSelected = selectedLocations.includes(location.id);
            return (
              <div
                key={location.id}
                className={`location-card ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleLocation(location.id)}
              >
                <div className="location-icon">
                  <EnvironmentOutlined />
                </div>
                <div className="location-info">
                  <div className="location-name">{location.name}</div>
                  <div className="location-address">{location.address}</div>
                </div>
                {isSelected && (
                  <div className="check-mark">
                    <CheckOutlined />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="campaign-footer">
        <div className="footer-info">
          <strong>{selectedLocations.length}</strong> selected
        </div>
        <button 
          className="continue-btn" 
          onClick={handleContinue}
          disabled={selectedLocations.length === 0}
        >
          Continue
          <DownOutlined />
        </button>
      </div>
    </div>
  );
};

export default SelectLocations;
